import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Check,
  ChevronDown,
  Filter,
  Tag as TagIcon,
  X,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  applyTag,
  detachTag,
  listTagAssignments,
  listTags,
} from "@/lib/api";
import { formatApiError } from "@/lib/api-error";

/**
 * Utility — calcolo automatico del contrasto WCAG per rendering
 * accessibile del chip. Il bg del chip è `color + 15% alpha` (quasi
 * bianco), quindi il testo deve essere sempre scuro. Se il color del
 * tag è già scuro (luminance < 0.5) può fungere direttamente da fg;
 * per colori chiari (giallo, verde chiaro) il fg diventa dark slate
 * per garantire contrasto sufficiente su bg semi-trasparente.
 */
function contrastText(hex) {
  const c = (hex || "").replace("#", "");
  if (c.length !== 6) return "#0F172A";
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  // Relative luminance (sRGB)
  const lum =
    (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#0F172A" : "#FFFFFF";
}

/**
 * Sceglie il colore di testo del chip garantendo contrasto WCAG sul
 * bg semi-trasparente (color @ 15%).
 * - Colori chiari (lum > 0.55) → testo dark slate #0F172A
 * - Colori scuri → testo con lo stesso color del tag (buono contrast
 *   sul bg quasi bianco)
 */
function chipTextColor(hex) {
  const c = (hex || "").replace("#", "");
  if (c.length !== 6) return "#0F172A";
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? "#0F172A" : hex;
}

function hexToRgba(hex, alpha) {
  const c = (hex || "").replace("#", "");
  if (c.length !== 6) return `rgba(100,116,139,${alpha})`;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * TagChip — visualizza un tag con contrasto automatico. Se onRemove è
 * fornito, mostra il bottone × per staccare.
 */
export function TagChip({ tag, onRemove, size = "md", testId }) {
  if (!tag) return null;
  const bg = hexToRgba(tag.color, 0.15);
  const border = hexToRgba(tag.color, 0.4);
  const fg =
    tag.is_active === false ? "#78716C" : chipTextColor(tag.color);
  const height = size === "sm" ? "h-[18px]" : "h-[22px]";
  const px = size === "sm" ? "px-1.5" : "px-2";
  const text = size === "sm" ? "text-[10px]" : "text-[11px]";
  return (
    <span
      data-testid={testId || `tag-chip-${tag.id}`}
      title={
        tag.is_active === false
          ? `${tag.name} (archiviato)`
          : tag.description || tag.name
      }
      className={`inline-flex items-center gap-1 rounded-full border font-medium ${height} ${px} ${text} ${tag.is_active === false ? "opacity-60" : ""}`}
      style={{ background: bg, borderColor: border, color: fg }}
    >
      <span
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ background: tag.color }}
        aria-hidden="true"
      />
      <span className="max-w-[140px] truncate">{tag.name}</span>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(tag);
          }}
          data-testid={`tag-chip-remove-${tag.id}`}
          title="Rimuovi etichetta"
          className="ml-0.5 rounded-full opacity-70 hover:bg-black/10 hover:opacity-100"
        >
          <X size={9} strokeWidth={2.4} />
        </button>
      )}
    </span>
  );
}

/**
 * TagList — rendering compatto di N chip (icona-only se lista lunga).
 * Se onRemove è fornito, ogni chip mostra il pulsante × per rimuovere
 * il tag dal movimento (l'anagrafica del tag resta intatta).
 */
export function TagList({ tags, size = "sm", max = 4, onRemove }) {
  if (!tags?.length) return null;
  const visible = tags.slice(0, max);
  const overflow = tags.length - visible.length;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {visible.map((t) => (
        <TagChip key={t.id} tag={t} size={size} onRemove={onRemove} />
      ))}
      {overflow > 0 && (
        <span
          className="inline-flex h-[18px] items-center rounded-full bg-stone-100 px-1.5 text-[10px] font-medium text-stone-500"
          title={tags
            .slice(max)
            .map((t) => t.name)
            .join(", ")}
        >
          +{overflow}
        </span>
      )}
    </span>
  );
}

/**
 * TagAssignmentPopover — popover per applicare/rimuovere tag su una
 * singola entità (entity_type + entity_id). Live update in ottimistico
 * con rollback in caso di errore.
 */
export function TagAssignmentPopover({
  entityType,
  entityId,
  currentTagIds = [],
  onChange,
  trigger,
  testIdPrefix = "tag-picker",
}) {
  const [open, setOpen] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [selected, setSelected] = useState(new Set(currentTagIds));
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setSelected(new Set(currentTagIds));
  }, [currentTagIds.join(",")]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const data = await listTags();
        setAllTags(data || []);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allTags;
    return allTags.filter((t) => (t.name || "").toLowerCase().includes(q));
  }, [allTags, search]);

  const toggle = async (tag) => {
    if (busy) return;
    setBusy(true);
    const wasSelected = selected.has(tag.id);
    const next = new Set(selected);
    if (wasSelected) next.delete(tag.id);
    else next.add(tag.id);
    setSelected(next);
    try {
      if (wasSelected) {
        await detachTag(tag.id, entityType, entityId);
      } else {
        await applyTag(tag.id, entityType, entityId);
      }
      onChange?.(Array.from(next));
    } catch (err) {
      // Rollback ottimistico
      setSelected(selected);
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || (
          <button
            type="button"
            data-testid={`${testIdPrefix}-trigger`}
            title="Applica etichetta"
            className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-white px-2 py-1 text-[11px] text-stone-600 hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
          >
            <TagIcon size={11} strokeWidth={2} />
            <span>Etichette</span>
            <ChevronDown size={9} strokeWidth={2} />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        data-testid={`${testIdPrefix}-panel`}
        className="w-[240px] p-2"
      >
        <input
          type="search"
          placeholder="Cerca etichetta…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid={`${testIdPrefix}-search`}
          className="mb-2 h-7 w-full rounded border border-stone-200 bg-stone-50 px-2 text-[11px] outline-none focus:border-primary/40 focus:bg-white"
        />
        <div className="max-h-56 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="py-3 text-center text-[11px] text-stone-400">
              Nessuna etichetta trovata
            </div>
          )}
          {filtered.map((t) => {
            const isSelected = selected.has(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggle(t)}
                disabled={busy}
                data-testid={`${testIdPrefix}-option-${t.id}`}
                className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-[11px] hover:bg-stone-50 disabled:opacity-50"
              >
                <span
                  className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border"
                  style={{
                    borderColor: t.color,
                    background: isSelected ? t.color : "transparent",
                  }}
                  aria-hidden="true"
                >
                  {isSelected && (
                    <Check
                      size={9}
                      strokeWidth={3}
                      style={{ color: contrastText(t.color) }}
                    />
                  )}
                </span>
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: t.color }}
                />
                <span className="truncate text-stone-700">{t.name}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * TagFilter — controllo di filtro OR/AND su lista di tag.
 * Emette (selectedIds[], mode 'or'|'and') via onChange.
 */
export function TagFilter({ selectedIds = [], mode = "or", untaggedOnly = false, onChange, testIdPrefix = "tag-filter" }) {
  const [open, setOpen] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await listTags();
        setAllTags(data || []);
      } catch {
        /* silent */
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allTags;
    return allTags.filter((t) => (t.name || "").toLowerCase().includes(q));
  }, [allTags, search]);

  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // Attivare una selezione specifica disattiva automaticamente
    // "solo senza etichetta" (le due modalità sono mutuamente esclusive).
    onChange?.({ selectedIds: Array.from(next), mode, untaggedOnly: false });
  };

  const clear = () => onChange?.({ selectedIds: [], mode, untaggedOnly: false });
  const switchMode = () =>
    onChange?.({ selectedIds, mode: mode === "or" ? "and" : "or", untaggedOnly });
  const toggleUntagged = () => {
    // Attivare "senza etichetta" azzera la selezione (non ha senso
    // combinare "senza etichetta" con "con etichetta X").
    onChange?.({ selectedIds: [], mode, untaggedOnly: !untaggedOnly });
  };

  // Badge counter mostra "senza etichetta" attivo con un pallino distintivo
  const badgeContent = untaggedOnly
    ? "∅"
    : selectedIds.length > 0
      ? selectedIds.length
      : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testIdPrefix}-trigger`}
          className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-white px-2 py-1 text-[11px] text-stone-600 hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
        >
          <Filter size={11} strokeWidth={2} />
          <span>Etichette</span>
          {badgeContent !== null && (
            <span className="ml-1 rounded-full bg-primary/10 px-1.5 text-[9px] font-semibold text-primary">
              {badgeContent}
            </span>
          )}
          <ChevronDown size={9} strokeWidth={2} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        data-testid={`${testIdPrefix}-panel`}
        className="w-[260px] p-2"
      >
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">
            Filtra per etichetta
          </div>
          <button
            type="button"
            onClick={switchMode}
            data-testid={`${testIdPrefix}-mode`}
            title="Cambia modalità"
            disabled={untaggedOnly}
            className="rounded bg-stone-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-stone-600 hover:bg-stone-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {mode === "or" ? "Almeno una" : "Tutte"}
          </button>
        </div>
        {/* Toggle "Solo senza etichetta" — v1.58.1 */}
        <button
          type="button"
          onClick={toggleUntagged}
          data-testid={`${testIdPrefix}-untagged`}
          className={`mb-2 flex w-full items-center gap-2 rounded border px-2 py-1 text-left text-[11px] transition-colors ${
            untaggedOnly
              ? "border-primary/40 bg-primary/5 text-primary"
              : "border-stone-200 bg-stone-50/50 text-stone-600 hover:bg-stone-100"
          }`}
        >
          <span
            className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
              untaggedOnly ? "border-primary bg-primary" : "border-stone-300 bg-white"
            }`}
          >
            {untaggedOnly && <Check size={9} strokeWidth={3} className="text-white" />}
          </span>
          <span className="flex-1 truncate font-medium">Solo senza etichetta</span>
        </button>
        <input
          type="search"
          placeholder="Cerca…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid={`${testIdPrefix}-search`}
          disabled={untaggedOnly}
          className="mb-2 h-7 w-full rounded border border-stone-200 bg-stone-50 px-2 text-[11px] outline-none focus:border-primary/40 focus:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
        />
        <div className={`max-h-52 overflow-y-auto ${untaggedOnly ? "pointer-events-none opacity-40" : ""}`}>
          {filtered.map((t) => {
            const isSel = selectedIds.includes(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggle(t.id)}
                data-testid={`${testIdPrefix}-option-${t.id}`}
                className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-[11px] hover:bg-stone-50"
              >
                <span
                  className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border"
                  style={{
                    borderColor: t.color,
                    background: isSel ? t.color : "transparent",
                  }}
                >
                  {isSel && (
                    <Check
                      size={9}
                      strokeWidth={3}
                      style={{ color: contrastText(t.color) }}
                    />
                  )}
                </span>
                <span className="h-2 w-2 rounded-full" style={{ background: t.color }} />
                <span className="truncate text-stone-700">{t.name}</span>
              </button>
            );
          })}
        </div>
        {(selectedIds.length > 0 || untaggedOnly) && (
          <button
            type="button"
            onClick={clear}
            data-testid={`${testIdPrefix}-clear`}
            className="mt-2 w-full rounded bg-stone-50 py-1 text-[10px] font-medium text-stone-500 hover:bg-stone-100"
          >
            Pulisci filtri
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Hook: recupera i tag applicati a una lista di righe con entity_type
 * eterogenei (es. Prima Nota: payment / collection / daily_receipt).
 * Raggruppa gli id per entity_type e lancia 1 batch call per gruppo
 * (max 3) — evita N+1 e mantiene bookkeeping unificato per row.id.
 *
 * @param rows array di oggetti sorgente
 * @param resolveEntity funzione (row) => { entityType, entityId, rowKey }
 *   Se ritorna null la riga viene ignorata (nessun tag possibile).
 *   `rowKey` è opzionale: default row.id — è la chiave con cui `tagsByEntity`
 *   viene indicizzato lato caller (utile quando row.id ha un prefisso).
 * @returns { tagsByEntity, refetch, resolveByRowKey } — la mappa è
 *   chiavata su `rowKey`, e resolveByRowKey ritorna la coppia
 *   {entityType, entityId} originale (serve per apply/detach).
 */
export function useEntityTagsPolymorphic(rows, resolveEntity) {
  const [tagsById, setTagsById] = useState({});
  const [assignmentsByRowKey, setAssignmentsByRowKey] = useState({});

  // Precomputa il mapping row → {entityType, entityId, rowKey}
  const mapped = useMemo(() => {
    const out = [];
    for (const row of rows || []) {
      const info = resolveEntity(row);
      if (!info) continue;
      out.push({
        rowKey: info.rowKey || row.id,
        entityType: info.entityType,
        entityId: info.entityId,
      });
    }
    return out;
  }, [rows, resolveEntity]);

  // Deduplica chiavi per il fetch (uno stesso occurrence_id può comparire
  // due volte nella lista se ci sono più righe collegate).
  const fetchKey = useMemo(() => {
    return mapped
      .map((m) => `${m.entityType}:${m.entityId}`)
      .sort()
      .join("|");
  }, [mapped]);

  const refetch = async () => {
    if (mapped.length === 0) {
      setAssignmentsByRowKey({});
      return;
    }
    try {
      // Raggruppa per entity_type
      const groups = {};
      for (const m of mapped) {
        if (!groups[m.entityType]) groups[m.entityType] = new Set();
        groups[m.entityType].add(m.entityId);
      }
      const apiModule = await import("@/lib/api");
      const [allTags, ...batches] = await Promise.all([
        listTags({ includeArchived: true }),
        ...Object.entries(groups).map(([et, idSet]) =>
          apiModule
            .listTagAssignmentsBatch(et, Array.from(idSet))
            .then((data) => ({ entityType: et, data: data || {} })),
        ),
      ]);
      const byId = {};
      for (const t of allTags || []) byId[t.id] = t;
      // batchByEntityType: { entity_type: { entity_id: [tag_id,...] } }
      const batchByType = {};
      for (const b of batches) batchByType[b.entityType] = b.data;
      // Ora costruisco assignments indicizzati per rowKey
      const byRowKey = {};
      for (const m of mapped) {
        const tagIds = batchByType[m.entityType]?.[m.entityId] || [];
        byRowKey[m.rowKey] = tagIds;
      }
      setTagsById(byId);
      setAssignmentsByRowKey(byRowKey);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    refetch();
  }, [fetchKey]);

  const tagsByEntity = useMemo(() => {
    const out = {};
    for (const m of mapped) {
      const tagIds = assignmentsByRowKey[m.rowKey] || [];
      out[m.rowKey] = tagIds.map((id) => tagsById[id]).filter(Boolean);
    }
    return out;
  }, [mapped, assignmentsByRowKey, tagsById]);

  const resolveByRowKey = useMemo(() => {
    const idx = {};
    for (const m of mapped) idx[m.rowKey] = { entityType: m.entityType, entityId: m.entityId };
    return (rowKey) => idx[rowKey] || null;
  }, [mapped]);

  return { tagsByEntity, refetch, resolveByRowKey, tagsById };
}


/**
 * Hook: recupera i tag applicati a una lista di entity_ids in un solo
 * batch e restituisce `{ tagsByEntity, refetch }`. Utile per popolare
 * i chip inline nelle tabelle senza N+1.
 */
export function useEntityTags(entityType, entityIds) {
  const [tagsById, setTagsById] = useState({});
  const [assignmentsByEntity, setAssignmentsByEntity] = useState({});
  const key = entityIds?.join(",") || "";

  const refetch = async () => {
    if (!entityIds?.length) {
      setAssignmentsByEntity({});
      return;
    }
    try {
      const [allTags, batch] = await Promise.all([
        listTags({ includeArchived: true }),
        import("@/lib/api").then((m) =>
          m.listTagAssignmentsBatch(entityType, entityIds),
        ),
      ]);
      const byId = {};
      for (const t of allTags || []) byId[t.id] = t;
      setTagsById(byId);
      setAssignmentsByEntity(batch || {});
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    refetch();
  }, [entityType, key]);

  const tagsByEntity = useMemo(() => {
    const out = {};
    for (const eid of entityIds || []) {
      const tagIds = assignmentsByEntity[eid] || [];
      out[eid] = tagIds
        .map((id) => tagsById[id])
        .filter(Boolean);
    }
    return out;
  }, [entityIds, assignmentsByEntity, tagsById]);

  return { tagsByEntity, refetch, tagsById };
}
